// src/store/modules/inventory.js
import {
    fetchLedger as apiFetchLedger,
    createEntry as apiCreateEntry,
    fetchSummary as apiFetchSummary,
    fetchHistory as apiFetchHistory,
    deleteByReference as apiDeleteByReference,
  } from '@/api/inventory'
  
  export default {
    namespaced: true,
    state: () => ({
      entries: [],
      pagination: { page: 1, pageSize: 10, total: 0 },
      totals: { qtyDelta: 0 },
      summary: [],               // [{ sku, name, totalQty, adjustmentCount }]
      historyBySku: {},          // { [sku]: [...] }
      filters: { search: '', reason: '' },
      loading: false,
      error: null
    }),
    mutations: {
      SET_LOADING(state, v) { state.loading = v },
      SET_ERROR(state, e) { state.error = e },
      SET_ENTRIES(state, { entries, pagination, totals }) {
        state.entries = entries
        state.pagination = pagination
        state.totals = totals || { qtyDelta: 0 }
      },
      SET_PAGE(state, page) { state.pagination.page = page },
      SET_PAGE_SIZE(state, size) { state.pagination.pageSize = size },
      SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters } }, 
      ADD_ENTRY(state, entry) { state.entries.unshift(entry) },
      SET_SUMMARY(state, summary) { state.summary = summary },
      SET_HISTORY(state, { sku, history }) { state.historyBySku = { ...state.historyBySku, [sku]: history } }
    },
    actions: {
      async fetchLedger({ commit, state }, opts = {}) {
        commit('SET_LOADING', true); commit('SET_ERROR', null)
        try {
          const params = {
            search: state.filters.search,
            reason: state.filters.reason,
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
            ...opts
          }
          const res = await apiFetchLedger(params)
          commit('SET_ENTRIES', {
            entries: res.entries || [],
            pagination: res.pagination || { page: 1, pageSize: 10, total: 0 },
            totals: res.totals || { qtyDelta: 0 }
          })
          return res
        } catch (e) {
          commit('SET_ERROR', e.response?.data || e.message); throw e
        } finally { commit('SET_LOADING', false) }
      },
      setFilters({ commit, dispatch }, filters) {
        commit('SET_FILTERS', filters)
        commit('SET_PAGE', 1)
        return dispatch('fetchLedger')
      },
      setPage({ commit, dispatch }, page) {
        commit('SET_PAGE', page)
        return dispatch('fetchLedger')
      },
      setPageSize({ commit, dispatch }, size) {
        commit('SET_PAGE_SIZE', size)
        commit('SET_PAGE', 1)
        return dispatch('fetchLedger')
      },
      async createEntry({ commit }, payload) {
        commit('SET_LOADING', true); commit('SET_ERROR', null)
        try {
          const res = await apiCreateEntry(payload)
          const entry = res.entry ?? res
          // optional optimistic add
          commit('ADD_ENTRY', entry)
          return entry
        } catch (e) {
          commit('SET_ERROR', e.response?.data || e.message); throw e
        } finally { commit('SET_LOADING', false) }
      },
      async fetchSummary({ commit }) {
        commit('SET_LOADING', true); commit('SET_ERROR', null)
        try {
          const res = await apiFetchSummary()
          // accept either an array or { summary: [...] }
          const summary = Array.isArray(res) ? res : (res?.summary || [])
          commit('SET_SUMMARY', summary)
          return summary
        } catch (e) {
          commit('SET_ERROR', e.response?.data || e.message); throw e
        } finally {
          commit('SET_LOADING', false)
        }
      },
      async fetchHistory({ commit }, sku) {
        if (!sku) return []
        commit('SET_LOADING', true); commit('SET_ERROR', null)
        try {
          const res = await apiFetchHistory(sku)
          // accept either an array or { history: [...] }
          const history = Array.isArray(res) ? res : (res?.history || [])
          commit('SET_HISTORY', { sku, history })
          return history
        } catch (e) {
          commit('SET_ERROR', e.response?.data || e.message); throw e
        } finally {
          commit('SET_LOADING', false)
        }
      },
      async deleteByReference(_ctx, referenceId) {
        if (!referenceId) return { ok: true, deletedCount: 0 }
        try {
          return await apiDeleteByReference(referenceId)
        } catch (e) {
          console.log(e)
          throw e
        }
      },
    },
    getters: {
      ledger: state => state.entries,
      isLoading: state => state.loading,
      lastError: state => state.error,
      page: state => state.pagination.page,
      pageSize: state => state.pagination.pageSize,
      totalRows: state => state.pagination.total,
      qtyDeltaTotal: state => state.totals.qtyDelta,
      summary: state => state.summary,
      historyForSku: state => sku => state.historyBySku[sku] || []
    }
  }
  